import { computed, reactive, type Ref } from 'vue'
import type { GlobalProgressState, ProgressJob } from './types'
import { isFunction } from '@/utils/is'
import { animate } from '@/utils/animation/animate'
import { clamp, lerp } from '@/utils/math'
import { sleep } from '@/utils/time'

const DELAY_JOB = () => sleep(60)

const state = reactive<GlobalProgressState>({
  schedule: [],
  index: 0,
  handle: null,
  running: false,
  currentLength: 0,
  progress: 0,
  visualProgress: 0,
  loopStartedAt: 0,
  loopDelta: 0
})

type UseGlobalProgress = {
  progress: Ref<number>
  running: Ref<boolean>
  length: Ref<number>
  push: (job: ProgressJob) => void
  start: () => Promise<void>
  stop: () => void
  clear: () => void
}

export const useGlobalProgress = (): UseGlobalProgress => {
  const push = (job: ProgressJob) => {
    state.schedule = [...state.schedule, DELAY_JOB, job, DELAY_JOB]
  }

  const running = computed(() => state.running)

  const loop = async () => {
    const q = [...state.schedule]
    let next: ProgressJob | undefined = undefined

    const unit = 1.0 / Math.max(1.0, q.length)

    state.index = 0
    let timeBefore: number = performance.now()

    while ((next = q.pop())) {
      const now = performance.now()
      const delta = now - timeBefore
      timeBefore = now
      state.loopDelta = delta / 1000

      if (isFunction(next)) {
        await next()
      } else {
        await next
      }
      state.index += 1
      state.progress += unit
    }

    state.running = false
  }

  const start = () => {
    state.index = 0
    state.running = true
    state.currentLength = state.schedule.length
    state.loopStartedAt = performance.now()

    return new Promise<void>((resolve) => {
      queueMicrotask(() => loop().catch((e) => console.error(e)))

      const anim = animate(
        (_time, _delta, elapsed) => {
          if (!state.running || state.index >= state.schedule.length) {
            anim.stop()
            resolve()
          }

          const unit = 1.0 / Math.max(1.0, state.schedule.length)
          const nextIndex = state.index + 1.0

          const f = (elapsed / 1000 / unit) * unit
          state.visualProgress = clamp(lerp(state.progress, nextIndex * unit, f), 0, 1)
        },
        { immediate: true, infinite: true }
      )
    })
  }

  const stop = () => {
    state.index = 0
    state.running = false
    state.loopDelta = 0
    state.loopStartedAt = 0
  }

  const clear = () => {
    state.index = 0
    state.progress = 0
    state.running = false
    state.currentLength = 0
    state.visualProgress = 0
    state.loopDelta = 0
    state.loopStartedAt = 0
    state.schedule = []
  }

  const length = computed(() => state.schedule.length)
  const progress = computed(() => state.visualProgress)
  return { push, progress, running, length, start, stop, clear }
}
